/**
 * Always-visible directory search field (magnifier + draft + clear).
 * Debounce / dirty-gate ownership stays in DirectoryListFilters.
 */

export interface DirectorySearchFilterTriggerProps {
  readonly onClick: () => void;
  readonly ariaLabel: string;
  readonly testId: string;
  readonly expanded?: boolean;
  readonly active?: boolean;
}

export interface DirectorySearchFieldProps {
  readonly draft: string;
  readonly onDraftChange: (value: string) => void;
  /** Enter / explicit commit — parent pushes draft to query immediately. */
  readonly onCommit: (value: string) => void;
  readonly onClear: () => void;
  readonly placeholder: string;
  readonly ariaLabel: string;
  readonly clearAriaLabel: string;
  readonly testIdPrefix: string;
  readonly className?: string;
  /**
   * `dominant` — taller / stronger chrome when a surface intentionally elevates search
   * above directory-toolbar weight. Default matches DirectoryFindToolbar search.
   */
  readonly emphasis?: 'default' | 'dominant';
  /** Optional in-bar filter trigger (mockup parity — icon inside search chrome). */
  readonly filterTrigger?: DirectorySearchFilterTriggerProps;
}

function FiltersIcon({ className }: { readonly className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M4 7h16" />
      <path d="M7 12h10" />
      <path d="M10 17h4" />
    </svg>
  );
}

function MagnifierIcon({ className }: { readonly className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

export function DirectorySearchField(props: DirectorySearchFieldProps): JSX.Element {
  const {
    draft,
    onDraftChange,
    onCommit,
    onClear,
    placeholder,
    ariaLabel,
    clearAriaLabel,
    testIdPrefix,
    className,
    emphasis = 'default',
    filterTrigger,
  } = props;
  const dominant = emphasis === 'dominant';
  const hasClear = draft.length > 0;
  const hasFilter = filterTrigger !== undefined;
  const trailingControlCount = (hasClear ? 1 : 0) + (hasFilter ? 1 : 0);
  let inputTrailingPadding: string;
  if (trailingControlCount === 0) {
    inputTrailingPadding = dominant ? 'pr-4' : 'pr-3';
  } else if (trailingControlCount === 1) {
    inputTrailingPadding = 'pr-12';
  } else {
    inputTrailingPadding = 'pr-[4.75rem]';
  }

  return (
    <div
      className={['relative min-w-0', className].filter(Boolean).join(' ')}
      data-search-emphasis={emphasis}
    >
      <span
        className={[
          'pointer-events-none absolute top-1/2 -translate-y-1/2 text-[var(--color-on-surface-muted)]',
          dominant ? 'left-3.5' : 'left-3',
        ].join(' ')}
        aria-hidden="true"
      >
        <MagnifierIcon className={dominant ? 'h-6 w-6' : 'h-5 w-5'} />
      </span>
      <input
        type="search"
        value={draft}
        onChange={(event) => {
          onDraftChange(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            onCommit(draft);
            return;
          }
          if (event.key === 'Escape' && draft.length > 0) {
            event.preventDefault();
            event.stopPropagation();
            onClear();
          }
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={[
          'w-full bg-[var(--color-surface-elevated)] text-[var(--color-on-surface)]',
          dominant
            ? [
                'min-h-12 rounded-md border-2 border-[var(--color-border-strong)]',
                'py-3.5 pl-12 text-base font-medium shadow-sm',
                inputTrailingPadding,
              ].join(' ')
            : [
                'rounded-sm border border-[var(--color-border-strong)]',
                'py-3 pl-10 text-sm',
                inputTrailingPadding,
              ].join(' '),
          'placeholder:text-[var(--color-on-surface-muted)]',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
          // Hide native clear so it does not stack on the custom × control.
          '[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none',
        ].join(' ')}
        data-testid={`${testIdPrefix}-search`}
      />
      {hasClear ? (
        <button
          type="button"
          className={[
            'absolute top-1/2 inline-flex h-10 min-h-10 w-10 min-w-10 -translate-y-1/2',
            'items-center justify-center rounded-full text-xl leading-none text-[var(--color-on-surface-muted)]',
            'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-on-surface)]',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
            hasFilter ? 'right-11' : 'right-1.5',
          ].join(' ')}
          onClick={onClear}
          aria-label={clearAriaLabel}
          data-testid={`${testIdPrefix}-clear`}
        >
          ×
        </button>
      ) : null}
      {hasFilter ? (
        <button
          type="button"
          className={[
            'absolute right-1.5 top-1/2 inline-flex h-10 min-h-10 w-10 min-w-10 -translate-y-1/2',
            'items-center justify-center rounded-full text-[var(--color-on-surface-muted)]',
            'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-on-surface)]',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
            filterTrigger.active
              ? 'text-[var(--color-action-primary)] ring-1 ring-[var(--color-action-primary)]'
              : '',
          ].join(' ')}
          onClick={filterTrigger.onClick}
          aria-label={filterTrigger.ariaLabel}
          aria-expanded={filterTrigger.expanded ?? false}
          aria-haspopup="dialog"
          data-testid={filterTrigger.testId}
        >
          <FiltersIcon className="h-5 w-5 shrink-0" />
          {filterTrigger.active ? (
            <span
              className="absolute right-1 top-1 h-2 w-2 rounded-full bg-[var(--color-action-primary)]"
              aria-hidden="true"
            />
          ) : null}
        </button>
      ) : null}
    </div>
  );
}
